import { Box, Divider, IconButton, Typography } from "@mui/material";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import ErrorMessage, {
  FullPageError,
} from "@/components/common/errors/errorMessage";
import { LogoutIcon } from "@/components/common/icons";
import PageCircularLoader from "@/components/common/loaders/pageCircularLoader";
import MedspaInfoCard from "@/components/common/medspaInfoCard";
import PageContainerWrapper from "@/components/common/pageContainer/pageContainerWrapper";
import PageTabs from "@/components/common/pageTabs/pageTabs";
import AffirmPolicy from "@/components/common/paymentPlans/affirmPolicy";
import ExplorePaymentPlansSlider from "@/components/common/paymentPlans/explorePaymentPlansSlider";
import { FullWidthTopBar } from "@/components/common/topBar/fullWidthTopBar";
import AppointmentsSection from "@/components/serviceFlow/client/appointmentsSection";
import OutstandingItems from "@/components/serviceFlow/client/outstadingItems";
import PaymentPlansPromotionBanner from "@/components/serviceFlow/client/paymentPlansPromotionBanner";
import ClientInvoices from "@/components/serviceFlow/clientDetails/invoices/clientInvoices";
import MembershipsSection from "@/components/serviceFlow/clientDetails/membershipsSection";
import FormSection from "@/components/serviceFlow/forms/formSection";
import WalletCardsSection from "@/components/serviceFlow/wallet/cards/walletCardSection";
import { VIOLET } from "@/config/mui/colorPalette";
import { useClientAccessToken } from "@/contexts/clientAccessTokenContext";
import useClientFormsByToken from "@/hooks/client/useClientFormsByToken";
import useClientOTPLogout from "@/hooks/client/useClientOTPLogout";
import { FeatureFlags } from "@/utils/featureFlags";
import useErrorLogger from "@/utils/useErrorLogger";

export enum TABS {
  APPOINTMENTS = "appointments",
  BILLING = "billing",
}

export default function ClientAppointmentsView({
  initialTab,
}: {
  initialTab: TABS;
}) {
  const {
    query: { medspaSlug },
    push,
  } = useRouter();

  const handleLogoutAction = useClientOTPLogout();

  const logError = useErrorLogger();

  const { clientAccessToken: token } = useClientAccessToken();

  const [selectedTab, setSelectedTab] = useState<TABS>(
    initialTab || TABS.APPOINTMENTS
  );

  const baseUrl = `/client/${medspaSlug}`;

  const onTabChangeHandler = (tab: TABS) => {
    setSelectedTab(tab);
    push(`/client/${medspaSlug}/${tab}`, undefined, { shallow: true });
  };

  const { loading, forms, visits, clientId, firstName, medspa, error } =
    useClientFormsByToken(token as string);

  useEffect(() => {
    if (error) {
      logError(error, "Error fetching client data");
    }
  }, [error, logError]);

  if (error) {
    return (
      <FullPageError>
        <ErrorMessage
          title="Error fetching client data"
          description="Please contact support team."
        />
      </FullPageError>
    );
  }

  if (loading) return <PageCircularLoader />;

  const {
    id: medspaId,
    name,
    address,
    contactPhone,
    contactEmail,
    additionalPublicInfo: { logo: logoUrl, enabledFeatures },
    configuration: {
      customizationEnabled,
      cherryApplicationLink,
      cherryPromotionEnabled,
      affirmPromotionEnabled,
    },
  } = medspa;

  const cherryIntegrationV1Enabled = enabledFeatures.includes(
    FeatureFlags.CHERRY_INTEGRATION_V1
  );

  return (
    <PageContainerWrapper
      topBarComponent={
        <FullWidthTopBar
          name={name}
          logoUrl={logoUrl}
          barTitle="Client Portal"
          customizationEnabled={customizationEnabled}
        >
          <IconButton onClick={handleLogoutAction}>
            <LogoutIcon color={VIOLET[90]} />
          </IconButton>
        </FullWidthTopBar>
      }
      additionalBannerComponent={
        cherryIntegrationV1Enabled && (
          <PaymentPlansPromotionBanner
            cherryPromotionEnabled={cherryPromotionEnabled}
            affirmPromotionEnabled={affirmPromotionEnabled}
          />
        )
      }
    >
      <Box
        sx={{
          m: 2,
          mt: 3,
        }}
      >
        <Typography variant="h1">Hi, {firstName}!</Typography>
      </Box>
      <MedspaInfoCard
        name={name}
        phone={contactPhone}
        email={contactEmail}
        address={address}
      />
      <PageTabs
        tabs={[
          {
            id: TABS.APPOINTMENTS,
            name: "Appointments",
            content: (
              <>
                <OutstandingItems
                  medspaId={medspaId}
                  visits={visits}
                  forms={forms}
                />
                {cherryIntegrationV1Enabled && (
                  <ExplorePaymentPlansSlider
                    clientId={clientId}
                    medspaId={medspa.id}
                    cherryPromotionEnabled={cherryPromotionEnabled}
                    affirmPromotionEnabled={affirmPromotionEnabled}
                    cherryApplicationLink={cherryApplicationLink}
                  />
                )}
                <Divider sx={{ my: 3 }} />
                <AppointmentsSection visits={visits} />
                <FormSection forms={forms} baseUrl={baseUrl} />
              </>
            ),
          },
          {
            id: TABS.BILLING,
            name: "Billing",
            content: (
              <>
                <WalletCardsSection clientId={clientId} expanded />
                <ClientInvoices clientId={clientId} expanded />
                <MembershipsSection
                  medspaName={medspa.name}
                  clientId={clientId}
                  isClientUser
                />
              </>
            ),
          },
        ]}
        selectedTab={selectedTab}
        onTabChange={onTabChangeHandler}
      />
      {cherryIntegrationV1Enabled && affirmPromotionEnabled && <AffirmPolicy />}
    </PageContainerWrapper>
  );
}
